import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logo from '../assets/logo.svg';
import axios from 'axios';

const Container = styled.div`
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  text-align: center;
`;

const Logo = styled.img`
  width: 200px;
  margin-bottom: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background-color: #45a049;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #4CAF50;
  cursor: pointer;
  margin-top: 15px;
  text-decoration: underline;
`;

const ErrorMessage = styled.div`
  color: red;
  margin-top: 10px;
  padding: 10px;
  border: 1px solid red;
  border-radius: 4px;
  background-color: #fee;
`;

const OAuthErrorMessage = styled.div`
  color: #d8000c;
  background-color: #ffbaba;
  border: 1px solid #d8000c;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: left;
`;

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [oauthError, setOauthError] = useState(null);

  // Handle OAuth error redirect from backend
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('OAuth error from backend redirect:', error, errorDescription);
      
      // Map error codes to user-friendly messages
      let errorMessage = '';
      switch (error) {
        case 'missing_parameters':
          errorMessage = 'OAuth authorization failed due to missing parameters. Please try again.';
          break;
        case 'invalid_state':
          errorMessage = 'OAuth authorization failed due to security validation. Please try again.';
          break;
        case 'token_exchange_failed':
          errorMessage = 'Failed to complete OAuth authorization with PG&E. Please try again.';
          break;
        case 'oauth_failed':
          errorMessage = 'OAuth authorization failed. Please try again.';
          break;
        default:
          errorMessage = errorDescription || `OAuth Error: ${error}`;
      }
      
      setOauthError(errorMessage);
      
      // Clear the URL parameters
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setOauthError(null); // Clear OAuth errors when user tries to login again

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const apiUrl = process.env.REACT_APP_API_URL;
      console.log('BACKEND API URL:', apiUrl); // Debug log

      if (!apiUrl) {
        throw new Error('BACKEND API URL is not configured');
      }

      const fullUrl = `${apiUrl}${endpoint}`;
      //const fullUrl = `/portal/api${endpoint}`;
      console.log('Making request to:', fullUrl);
      
      const response = await axios({
        method: 'POST',
        url: fullUrl,
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
        data: { email, password }
      });

      console.log('Response:', response.data);

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        navigate('/confirm');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  return (
    <Container>
      <Logo src={logo} alt="Logo" />
      
      {/* Display OAuth error if present */}
      {oauthError && (
        <OAuthErrorMessage>
          <strong>OAuth Authorization Failed</strong><br />
          {oauthError}
          <br /><br />
          <small>Please log in and try the OAuth process again.</small>
        </OAuthErrorMessage>
      )}
      
      <h2>{isLogin ? 'Login' : 'Sign Up'}</h2>
      <Form onSubmit={handleSubmit}>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Button type="submit">
          {isLogin ? 'Login' : 'Sign Up'}
        </Button>
      </Form>
      <ToggleButton onClick={() => setIsLogin(!isLogin)}>
        {isLogin ? 'Need an account? Sign up' : 'Already have an account? Login'}
      </ToggleButton>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Container>
  );
}

export default Login;
